import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { CATEGORY_OPTIONS } from '../../../constants/filterOptions';

const FilterSidebar = ({ filters, onFilterChange, onClearFilters, isOpen, onToggle, filterCounts = {}, maxPrice = 10000 }) => {
  // Usar CATEGORY_OPTIONS com contagens reais
  const categories = CATEGORY_OPTIONS.map(option => ({
    id: option.id,
    label: option.label,
    value: option.value,
    count: filterCounts?.categories?.[option.value] || 0
  }));

  // Calcular valor do slider baseado nos filtros
  const priceSliderValue = filters?.priceRange?.max !== null && filters?.priceRange?.max !== undefined
    ? filters.priceRange.max
    : maxPrice;


  const handleCategoryChange = (categoryId, checked) => {
    const updatedCategories = checked 
      ? [...(filters?.categories || []), categoryId]
      : (filters?.categories || [])?.filter(id => id !== categoryId);
    onFilterChange({ ...filters, categories: updatedCategories });
  };

  const handleSearchChange = (value) => {
    onFilterChange({ ...filters, searchTerm: value });
  };

  const handlePriceSliderChange = (value) => {
    const priceValue = parseInt(value);
    // Se o valor for igual ao máximo, remove o filtro (mostra todos)
    if (priceValue >= maxPrice) {
      onFilterChange({ 
        ...filters, 
        priceRange: { 
          min: null,
          max: null
        }
      });
    } else {
      onFilterChange({ 
        ...filters, 
        priceRange: { 
          min: null,
          max: priceValue
        }
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const FilterSection = ({ title, children, defaultOpen = true }) => {
    const [isExpanded, setIsExpanded] = useState(defaultOpen);

    return (
      <div className="border-b border-border pb-6 mb-6">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-left mb-4"
        >
          <h3 className="font-playfair font-semibold text-lg text-text-primary">{title}</h3>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-text-secondary" 
          />
        </button>
        {isExpanded && children}
      </div>
    );
  };


  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:sticky top-0 left-0 h-full lg:h-auto w-80 bg-card border-r border-border z-50 lg:z-auto
        transform transition-transform duration-300 lg:transform-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto
      `}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <h2 className="font-playfair font-bold text-xl text-text-primary">Filtros</h2>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-text-secondary hover:text-primary"
              >
                Limpar
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="lg:hidden"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <Input
              type="search"
              placeholder="Buscar produtos..."
              value={filters?.searchTerm || ''}
              onChange={(e) => handleSearchChange(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Price Range Slider */}
          <FilterSection title="Faixa de Preço">
            <div className="space-y-4">
              <div className="px-2">
                <input
                  type="range"
                  min="0"
                  max={maxPrice}
                  step="100"
                  value={priceSliderValue}
                  onChange={(e) => handlePriceSliderChange(e.target.value)}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #8B7355 0%, #8B7355 ${(priceSliderValue / maxPrice) * 100}%, #e5e7eb ${(priceSliderValue / maxPrice) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-primary font-medium">Até {formatPrice(priceSliderValue)}</span>
                <span className="text-text-secondary">{formatPrice(maxPrice)}</span>
              </div>
            </div>
            <style>{`
              input[type="range"]::-webkit-slider-thumb {
                appearance: none;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #8B7355;
                cursor: pointer;
                border: 2px solid #fff;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              }
              input[type="range"]::-moz-range-thumb {
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #8B7355;
                cursor: pointer;
                border: 2px solid #fff;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              }
            `}</style>
          </FilterSection>

          {/* Categories */}
          <FilterSection title="Categorias">
            <div className="space-y-3">
              {categories?.map((category) => (
                <div key={category?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={category?.label}
                    checked={(filters?.categories || [])?.includes(category?.id)}
                    onChange={(e) => handleCategoryChange(category?.id, e?.target?.checked)}
                  />
                  <span className="text-sm text-text-secondary">({category?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;